import React from 'react';
import { Box, Grid, Card, CardContent, Typography, Button, Switch, Divider, Avatar } from '@mui/material';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import SoupKitchenIcon from '@mui/icons-material/SoupKitchen';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import InventoryIcon from '@mui/icons-material/Inventory';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useApp } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';

export default function SubscriptionView() {
    const { subscribedModules, setSubscribedModules, showToast } = useApp();
    const { t } = useLanguage();

    const modulesList = [
        {
            id: 'pos',
            title: 'POS Terminal & Billing Hub',
            desc: 'Table floor mapping, billing settlement, customer receipts, and transaction history.',
            price: 999,
            icon: <PointOfSaleIcon sx={{ color: '#008751' }} />
        },
        {
            id: 'kds',
            title: 'Kitchen Display System (KDS)',
            desc: 'Real-time order tickets routing to kitchen screens with color-coded timers.',
            price: 499,
            icon: <SoupKitchenIcon sx={{ color: '#10b981' }} />
        },
        {
            id: 'captain',
            title: 'Captain PWA Order Simulator',
            desc: 'Mobile-friendly waiter interface to take orders directly at tables.',
            price: 399,
            icon: <PhoneAndroidIcon sx={{ color: '#3b82f6' }} />
        },
        {
            id: 'inventory',
            title: 'Inventory & Recipe Manager',
            desc: 'Recipe ingredient weights editor, inward purchases, outward wastage, and audit logs.',
            price: 799,
            icon: <InventoryIcon sx={{ color: '#8b5cf6' }} />
        },
        {
            id: 'accounting',
            title: 'Daily Accounting Ledger',
            desc: 'Cash drawer tally sheet tracker, variance analysis, and daily outflow expense book.',
            price: 499,
            icon: <AccountBalanceWalletIcon sx={{ color: '#ec4899' }} />
        },
        {
            id: 'compliance',
            title: 'FSSAI & Privacy Compliance Suite',
            desc: 'Allergen tracking, nutritional calorie disclosures, and DPDP Act customer consent manager.',
            price: 299,
            icon: <CheckCircleIcon sx={{ color: '#06b6d4' }} />
        },
        {
            id: 'bizAnalytics',
            title: 'Dinexa Biz Analytics App',
            desc: 'Owner companion app with 7-day revenue trend analysis and multi-outlet statistics dashboard.',
            price: 1999,
            icon: <TrendingUpIcon sx={{ color: '#0284c7' }} />
        },
        {
            id: 'zomato_swiggy',
            title: 'Zomato & Swiggy Aggregator Integration',
            desc: 'Live online order channels syncing directly to KDS & POS terminal.',
            price: 899,
            icon: <CheckCircleIcon sx={{ color: '#ea580c' }} />
        }
    ];

    const handleToggleModule = (moduleId) => {
        setSubscribedModules(prev => {
            // Keep at least POS enabled to avoid empty app locks, or allow disabling
            if (moduleId === 'pos' && prev.includes('pos')) {
                showToast('POS module is core and cannot be disabled.');
                return prev;
            }
            if (prev.includes(moduleId)) {
                return prev.filter(id => id !== moduleId);
            } else {
                return [...prev, moduleId];
            }
        });
    };

    const handleQuickSelectPreset = (preset) => {
        if (preset === 'basic') {
            setSubscribedModules(['pos']);
            showToast('Subscribed to Basic POS Plan!');
        } else if (preset === 'operations') {
            setSubscribedModules(['pos', 'kds', 'captain']);
            showToast('Subscribed to Kitchen Operations Plan!');
        } else if (preset === 'full') {
            setSubscribedModules(['pos', 'kds', 'captain', 'inventory', 'accounting']);
            showToast('Subscribed to Premium All-In-One SaaS Package!');
        }
    };

    // Calculate billing total
    const totalMonthlyCost = modulesList
        .filter(m => subscribedModules.includes(m.id))
        .reduce((sum, m) => sum + m.price, 0);

    return (
        <Box sx={{ p: 4, bgcolor: '#f9fafb', minHeight: 'calc(100vh - 70px)' }}>
            <Typography variant="h5" sx={{ fontWeight: 900, color: '#111827', mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <SubscriptionsIcon sx={{ color: '#008751', fontSize: 32 }} />
                {t('nav-subscriptions')}
            </Typography>

            <Grid container spacing={4}>
                {/* Modules configurator list */}
                <Grid item xs={12} md={8}>
                    <Card sx={{ borderRadius: 3, border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', mb: 4 }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#111827', mb: 1 }}>
                                Select Subscribed Modules (वैशिष्ट्ये निवडा)
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#6b7280', mb: 3 }}>
                                Pay only for what you use. Enable features instantly on the POS floor by toggling subscription controls.
                            </Typography>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {modulesList.map(mod => {
                                    const isSubscribed = subscribedModules.includes(mod.id);
                                    return (
                                        <Box
                                            key={mod.id}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                p: 2.5,
                                                borderRadius: 3,
                                                border: `1px solid ${isSubscribed ? '#008751' : '#e5e7eb'}`,
                                                bgcolor: isSubscribed ? 'rgba(79, 70, 229, 0.02)' : '#ffffff',
                                                '&:hover': { borderColor: '#008751' },
                                                transition: 'all 0.2s ease'
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexGrow: 1 }}>
                                                <Avatar sx={{ bgcolor: isSubscribed ? 'rgba(79, 70, 229, 0.1)' : '#f3f4f6', width: 44, height: 44, borderRadius: 2 }}>
                                                    {mod.icon}
                                                </Avatar>
                                                <Box sx={{ pr: 3 }}>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#111827' }}>
                                                        {mod.title}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mt: 0.5 }}>
                                                        {mod.desc}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                                <Box textAlign="right">
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#111827' }}>
                                                        ₹{mod.price}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                                                        / month
                                                    </Typography>
                                                </Box>
                                                <Switch
                                                    checked={isSubscribed}
                                                    onChange={() => handleToggleModule(mod.id)}
                                                    sx={{
                                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                                            color: '#06b6d4',
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(6, 182, 212, 0.08)',
                                                            },
                                                        },
                                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                            backgroundColor: '#06b6d4',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Billing Summary Box */}
                <Grid item xs={12} md={4}>
                    <Card sx={{ borderRadius: 3, border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', position: 'sticky', top: 20 }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#111827', mb: 2 }}>
                                Monthly Billing Summary
                            </Typography>

                            <Box sx={{ display: 'flex', flexDir: 'column', gap: 1.5, mb: 3 }}>
                                {modulesList.map(mod => {
                                    const isSubscribed = subscribedModules.includes(mod.id);
                                    if (!isSubscribed) return null;
                                    return (
                                        <Box key={mod.id} sx={{ display: 'flex', justify: 'space-between', alignItems: 'center' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <CheckCircleIcon sx={{ fontSize: 16, color: '#10b981' }} />
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#4b5563' }}>{mod.title}</Typography>
                                            </Box>
                                            <Typography variant="body2" sx={{ fontWeight: 800 }}>₹{mod.price}</Typography>
                                        </Box>
                                    );
                                })}
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            <Box sx={{ display: 'flex', justify: 'space-between', alignItems: 'center', mb: 3 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Total Subscription Cost:</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 900, color: '#008751' }}>₹{totalMonthlyCost}/mo</Typography>
                            </Box>

                            {/* Preset Buttons */}
                            <Typography variant="caption" sx={{ fontWeight: 800, color: '#9ca3af', mb: 1, display: 'block' }}>
                                POPULAR PRESETS & BUNDLES
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => handleQuickSelectPreset('basic')}
                                    sx={{ py: 1.2, fontWeight: 700, textTransform: 'none', borderRadius: 2, color: '#008751', borderColor: '#008751', '&:hover': { borderColor: '#006f42', bgcolor: 'rgba(0, 135, 81, 0.04)' } }}
                                >
                                    Basic Plan (POS Only)
                                </Button>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => handleQuickSelectPreset('operations')}
                                    sx={{ py: 1.2, fontWeight: 700, textTransform: 'none', borderRadius: 2, color: '#008751', borderColor: '#008751', '&:hover': { borderColor: '#006f42', bgcolor: 'rgba(0, 135, 81, 0.04)' } }}
                                >
                                    Kitchen Operations Bundle (POS + KDS + Captain)
                                </Button>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={() => handleQuickSelectPreset('full')}
                                    sx={{
                                        py: 1.5,
                                        fontWeight: 800,
                                        textTransform: 'none',
                                        borderRadius: 2,
                                        bgcolor: '#008751',
                                        boxShadow: '0 4px 14px 0 rgba(79, 70, 229, 0.4)',
                                        '&:hover': {
                                            bgcolor: '#006f42',
                                            boxShadow: '0 6px 20px 0 rgba(67, 56, 202, 0.4)'
                                        }
                                    }}
                                >
                                    SaaS Premium All-In (All Modules)
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
}
